/* eslint-disable react/no-multi-comp -- pre-existing: this file has always
   declared its sub-components inline. `lint:changed` only lints files a branch
   touches, so the violation surfaced when BON-528 edited one line here. The
   split into one-component-per-file wants its own change, where it can be
   reviewed on its merits. */
import { Pencil, Power, RotateCw } from 'lucide-react';
import { useState } from 'react';

import { mockedAuth0Sub } from '../mockedFields';
import { canTake, type UserRow } from '../userViewModel';

import MockedValue from './MockedValue';
import UserDetailHeader from './UserDetailHeader';
import UserDetailRecentActivity from './UserDetailRecentActivity';
import UserDetailRoleCard from './UserDetailRoleCard';
import UserDetailTabs, { type UserDetailTab } from './UserDetailTabs';

interface UserDetailPaneProps {
  user: UserRow;
  onClose: () => void;
  /** Open the deactivate confirm modal. */
  onDeactivate?: (user: UserRow) => void;
  /** Reactivate the user inline (no preflight; one click). */
  onReactivate?: (user: UserRow) => void;
  /** Open the edit-role modal. */
  onEdit?: (user: UserRow) => void;
}

function IdentityCard({ user }: { user: UserRow }) {
  return (
    <div className="mx-[18px] mb-3.5 px-3.5 py-3 bg-stone-50 dark:bg-[#1e2520] border border-stone-200 dark:border-stone-800 rounded-lg">
      <div className="grid grid-cols-[90px_1fr] gap-y-1.5 text-[11.5px]">
        <span className="text-stone-500 dark:text-stone-400">Joined</span>
        <span className="text-bark-800 dark:text-[#e8ece9]">{user.hireDate || '—'}</span>
        <span className="text-stone-500 dark:text-stone-400">Auth0 sub</span>
        <span className="text-bark-800 dark:text-[#e8ece9] font-mono text-[10.5px]">
          <MockedValue pending="return User.auth0Sub from /api/v1/staff">
            {mockedAuth0Sub(user.id)}
          </MockedValue>
        </span>
        <span className="text-stone-500 dark:text-stone-400">FH username</span>
        <span className="text-bark-800 dark:text-[#e8ece9] font-mono text-[10.5px]">
          {user.fareharborUsername || '—'}
        </span>
        {user.isStaff && (
          <>
            <span className="text-stone-500 dark:text-stone-400">Assignments</span>
            <span className="text-bark-800 dark:text-[#e8ece9]">
              <strong>{user.upcomingWorkshops}</strong> upcoming workshops
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function FooterActions({
  user,
  onDeactivate,
  onReactivate,
  onEdit,
}: {
  user: UserRow;
  onDeactivate?: (user: UserRow) => void;
  onReactivate?: (user: UserRow) => void;
  onEdit?: (user: UserRow) => void;
}) {
  const canReactivate = canTake(user, 'reactivate');
  const canDeactivate = canTake(user, 'deactivate');
  const canEdit = canTake(user, 'edit-roles') || canTake(user, 'edit-regions');
  if (!canReactivate && !canDeactivate && !canEdit) return null;
  return (
    <div className="px-[18px] py-3 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-[#1e2520] flex gap-2">
      {canReactivate && (
        <button
          type="button"
          onClick={() => onReactivate?.(user)}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border text-[12px] font-semibold text-moss-700 dark:text-moss-200 border-moss-200 dark:border-moss-800 bg-moss-50 dark:bg-moss-900/20 hover:bg-moss-100 dark:hover:bg-moss-900/40"
        >
          <RotateCw size={13} />
          Reactivate
        </button>
      )}
      {canDeactivate && (
        <button
          type="button"
          onClick={() => onDeactivate?.(user)}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border text-[12px] font-semibold text-clay-700 dark:text-clay-300 border-clay-200 dark:border-clay-800 bg-clay-50 dark:bg-clay-900/20 hover:bg-clay-100 dark:hover:bg-clay-900/40"
        >
          <Power size={13} />
          Deactivate
        </button>
      )}
      <div className="flex-1" />
      {canEdit && (
        <button
          type="button"
          onClick={() => onEdit?.(user)}
          className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-semibold text-white bg-moss-600 hover:bg-moss-700"
        >
          <Pencil size={13} />
          Edit role &amp; regions
        </button>
      )}
    </div>
  );
}

/** Right-hand detail pane for the selected user. Two tabs (Profile /
 *  Audit) live between the header and the per-tab body; the footer "Edit
 *  role & regions" + Deactivate actions are tab-independent. */
export default function UserDetailPane({
  user,
  onClose,
  onDeactivate,
  onReactivate,
  onEdit,
}: UserDetailPaneProps) {
  const [tab, setTab] = useState<UserDetailTab>('profile');
  return (
    <aside
      role="dialog"
      aria-modal="true"
      aria-labelledby={`user-detail-title-${user.id}`}
      className="w-[380px] flex-none bg-white dark:bg-[#1a1f1c] rounded-xl border border-stone-200 dark:border-stone-800 flex flex-col overflow-hidden"
    >
      <UserDetailHeader user={user} onClose={onClose} />
      <UserDetailTabs active={tab} onChange={setTab} />
      <div
        role="tabpanel"
        id={`user-detail-panel-${tab}`}
        aria-labelledby={`user-detail-tab-${tab}`}
        className="flex-1 overflow-y-auto pt-3"
      >
        {tab === 'profile' && (
          <>
            <UserDetailRoleCard user={user} />
            <IdentityCard user={user} />
          </>
        )}
        {tab === 'audit' && <UserDetailRecentActivity userId={user.id} userName={user.name} />}
      </div>
      <FooterActions
        user={user}
        onDeactivate={onDeactivate}
        onReactivate={onReactivate}
        onEdit={onEdit}
      />
    </aside>
  );
}

/* eslint-enable react/no-multi-comp -- pairs with file-top disable */
